import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types/work";

interface Props {
  slides: Work[];
}

export default function HeroCarousel({ slides }: Props) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = slides.length;

  const go = useCallback(
    (step: number) => {
      setIndex((prev) => (prev + step + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => go(1), 6500);
    return () => clearInterval(timer);
  }, [go, count]);

  if (count === 0) {
    return (
      <section className="w-full bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 flex flex-col items-center justify-center text-center gap-3">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-50/10">
            <i className="ri-inbox-archive-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-2xl">暂无推荐内容</h1>
          <p className="text-background-50/70 text-sm max-w-md">
            首页焦点内容暂时为空，你可以刷新页面，或先前往分类页浏览全部伦理影视作品。
          </p>
          <Link
            to="/category/erotic"
            className="mt-2 px-5 h-10 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors"
          >
            浏览伦理电影
          </Link>
        </div>
      </section>
    );
  }

  const active = slides[index];

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[460px] md:h-[580px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.backdrop || slide.poster}
            alt={`${slide.title}专题图`}
            title={`${slide.title} 韩国伦理影视专题`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
        </div>
      ))}

      <div className="relative h-full max-w-[1280px] mx-auto px-4 md:px-6 flex items-center">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
              焦点推荐
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs">
              {active.type === "movie" ? "伦理电影" : "伦理电视剧"}
            </span>
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 leading-tight">
            {active.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-background-50/80">
            <span>{active.year}</span>
            <span className="w-px h-3 bg-background-50/30" />
            <span>{active.genres.join(" · ")}</span>
            <span className="w-px h-3 bg-background-50/30" />
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill text-xs" /> {active.rating.toFixed(1)}
            </span>
          </div>
          <p className="mt-4 text-sm md:text-base text-background-50/75 leading-relaxed line-clamp-3">
            {active.synopsis[0]}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="px-5 h-11 inline-flex items-center gap-2 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors"
            >
              <i className="ri-play-circle-line text-lg" /> 查看详情
            </Link>
            <Link
              to={active.type === "movie" ? "/category/erotic" : "/category/drama"}
              className="px-5 h-11 inline-flex items-center gap-2 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors"
            >
              同类作品
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2">
        <button
          type="button"
          onClick={() => go(-1)}
          className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
      </div>
      <div className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2">
        <button
          type="button"
          onClick={() => go(1)}
          className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/40 hover:bg-background-50/70"
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}